import { labApi } from "@/api/lab";
import { ipdApi } from "@/api/ipd";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { useMemo, useState } from "react";
import { Search, Plus } from "lucide-react";
import CreateLabOrderDialog from "@/components/lab/CreateLabOrderDialog";
import CreateLabOrderBill from "@/components/lab/CreateLabOrderBill";
import ViewBill from "@/components/billing/ViewBill";
import { LabOrderStatus } from "@/types/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function CreateLabTestAppointment() {
    const [tab, setTab] = useState<"opd" | "ipd">("opd");

    /**
     * OPD / Non-IPD flow (patient first, then tests)
     * Uses LabOrder + AppointmentLabTest and supports Internal/External selection in CreateLabOrderDialog.
     */
    const [searchQueryPatient, setSearchQueryPatient] = useState('');
    const [createLabOrderDialogOpen, setCreateLabOrderDialogOpen] = useState(false);
    const [billingDialogOpen, setBillingDialogOpen] = useState(false);
    const [selectedLabOrder, setSelectedLabOrder] = useState<any>(null);
    const [viewBillDialogOpen, setViewBillDialogOpen] = useState(false);
    const [selectedBillId, setSelectedBillId] = useState<string | null>(null);
    const hospitalId = localStorage.getItem('hospitalId');
    const queryClient = useQueryClient();

    // Handler for opening billing dialog
    const handleGenerateBill = (order: any) => {
        setSelectedLabOrder(order);
        setBillingDialogOpen(true);
    };

    // Handler for successful billing
    const handleBillingSuccess = async () => {
        // Invalidate and refetch external lab orders to update status and actions
        await queryClient.invalidateQueries({ queryKey: ['external-lab-orders'] });
        await queryClient.refetchQueries({ queryKey: ['external-lab-orders'] });
        setBillingDialogOpen(false);
        setSelectedLabOrder(null);
    };

    // Handler for viewing bill
    const handleViewBill = (order: any) => {
        // Note: The billId should be included in the lab order response from the backend
        // For now, we'll use the order id as a placeholder - this needs to be updated
        // to use the actual billId associated with the lab order
        const billId = order.billId || `bill-${order.id}`; // Placeholder - needs actual billId
        setSelectedBillId(billId);
        setViewBillDialogOpen(true);
    };

    // Handler for successful bill operations
    const handleBillSuccess = () => {
        setViewBillDialogOpen(false);
        setSelectedBillId(null);
        // Optionally refresh the lab orders data
    };

    // Fetch external lab orders
    const { data: externalLabOrders } = useQuery<any>({
        queryKey: ['external-lab-orders'],
        queryFn: async () => {
            const response = await labApi.getExternalLabOrdersByHospital(hospitalId || '');
            return response.data?.data;
        },
    });

    /**
     * IPD flow (pick admitted patient, then tests; can be internal or external)
     * Creates IPDLabTest entries which flow into IPD billing as LAB items.
     */
    const [ipdPatientSearch, setIpdPatientSearch] = useState("");
    const [orderDialogOpen, setOrderDialogOpen] = useState(false);
    const [selectedAdmission, setSelectedAdmission] = useState<any | null>(null);
    const [processingType, setProcessingType] = useState<"INTERNAL" | "EXTERNAL">("INTERNAL");
    const [selectedExternalVendorId, setSelectedExternalVendorId] = useState("");
    const [selectedLabTestIds, setSelectedLabTestIds] = useState<string[]>([]);
    const [externalCostByTestId, setExternalCostByTestId] = useState<Record<string, string>>({});

    const { data: admittedAdmissions = [], isLoading: admissionsLoading } = useQuery<any[]>({
        queryKey: ["ipd-admissions-for-receptionist-create-test"],
        queryFn: async () => {
            // Include discharged admissions too (tests/billing may be entered after discharge).
            const res = await ipdApi.getAdmissions();
            return res.data?.data || [];
        },
        enabled: tab === "ipd",
    });

    const { data: labTestsMaster = [] } = useQuery<any[]>({
        queryKey: ["lab-tests-master"],
        queryFn: async () => {
            const res = await labApi.getLabTests();
            const raw = res.data as any;
            return Array.isArray(raw?.data) ? raw.data : raw?.data || [];
        },
        enabled: tab === "ipd",
    });

    const { data: externalVendors = [] } = useQuery<any[]>({
        queryKey: ["lab-external-vendors"],
        queryFn: async () => {
            const res = await labApi.listExternalVendors();
            return res.data?.data ?? [];
        },
        enabled: tab === "ipd",
        staleTime: 300000,
    });

    const filteredAdmissions = useMemo(() => {
        if (!ipdPatientSearch) return admittedAdmissions;
        const q = ipdPatientSearch.toLowerCase();
        return admittedAdmissions.filter((a: any) => {
            const p = a?.queue?.patient;
            const ipdNo = String(a?.queue?.ipdNumber ?? a?.id ?? "").toLowerCase();
            const ward = String(a?.wardName ?? a?.ward?.name ?? "").toLowerCase();
            const bed = String(a?.bedNumber ?? a?.bed?.bedNumber ?? "").toLowerCase();
            return (
                String(p?.name ?? "").toLowerCase().includes(q) ||
                String(p?.uhid ?? "").toLowerCase().includes(q) ||
                String(p?.phone ?? "").toLowerCase().includes(q) ||
                ipdNo.includes(q) ||
                ward.includes(q) ||
                bed.includes(q)
            );
        });
    }, [admittedAdmissions, ipdPatientSearch]);

    const openIpdOrderDialog = (admission: any) => {
        setSelectedAdmission(admission);
        setProcessingType("INTERNAL");
        setSelectedExternalVendorId("");
        setSelectedLabTestIds([]);
        setExternalCostByTestId({});
        setOrderDialogOpen(true);
    };

    const createIpdTestsMutation = useMutation({
        mutationFn: async () => {
            if (!selectedAdmission?.id) throw new Error("Admission is required");
            if (!selectedLabTestIds.length) throw new Error("Select at least 1 test");
            if (processingType === "EXTERNAL" && !selectedExternalVendorId) {
                throw new Error("Select external vendor");
            }

            const tests = labTestsMaster.filter((t: any) => selectedLabTestIds.includes(t.id));
            for (const test of tests) {
                let externalCost: number | undefined = undefined;
                if (processingType === "EXTERNAL") {
                    const raw = externalCostByTestId[test.id];
                    const val = raw === "" || raw === undefined ? NaN : Number(raw);
                    if (!Number.isFinite(val) || val < 0) {
                        throw new Error(`Enter valid hospital cost for ${test.name}`);
                    }
                    externalCost = val;
                }

                await ipdApi.createIPDLabTest({
                    admissionId: selectedAdmission.id,
                    testName: test.name,
                    testCode: test.code,
                    category: test.sampleType,
                    priority: "ROUTINE",
                    testCost: test.charge, // patient charge (selling price)
                    labTestId: test.id,
                    processingType,
                    externalVendorId: processingType === "EXTERNAL" ? selectedExternalVendorId : undefined,
                    externalCost,
                });
            }
            return { count: tests.length };
        },
        onSuccess: async (r: any) => {
            toast.success(`Ordered ${r?.count ?? selectedLabTestIds.length} test(s) for IPD`);
            setOrderDialogOpen(false);
            setSelectedAdmission(null);
            setSelectedLabTestIds([]);
            setSelectedExternalVendorId("");
            setExternalCostByTestId({});
            await queryClient.invalidateQueries({ queryKey: ["ipd-lab-tests"] });
        },
        onError: (e: any) => toast.error(e?.message || "Failed to order IPD tests"),
    });


    return (
        <div className="thms-page-shell">
            <div>
                <h1 className="text-2xl font-semibold">Create Test</h1>
                <p className="text-sm text-muted-foreground">
                    OPD: pick patient → tests → internal/external. IPD: pick admitted patient → tests → internal/external.
                </p>
            </div>

            <Tabs value={tab} onValueChange={(v: any) => setTab(v)}>
                <TabsList>
                    <TabsTrigger value="opd">OPD</TabsTrigger>
                    <TabsTrigger value="ipd">IPD</TabsTrigger>
                </TabsList>

                <TabsContent value="opd">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-semibold">OPD / Non‑IPD Lab Orders</h2>
                        <Button
                            onClick={() => setCreateLabOrderDialogOpen(true)}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Lab Order
                        </Button>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold mb-4">Orders</h3>
                        <div className="mb-4">
                            <div className="relative">
                                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                                <Input
                                    placeholder="Search by patient name, test name, or status..."
                                    value={searchQueryPatient}
                                    onChange={(e) => setSearchQueryPatient(e.target.value)}
                                    className="pl-8"
                                />
                            </div>
                        </div>
                        <Table numberOfRows={10}>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Patient</TableHead>
                                    <TableHead>Order Details</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {externalLabOrders?.filter((order: any) => {
                                    if (!searchQueryPatient) return true;
                                    const patientName = order.patient?.name?.toLowerCase() || '';
                                    const patientId = order.patient?.patientUniqueId?.toLowerCase() || '';
                                    const phone = order.patient?.phone || '';
                                    const notes = order.notes?.toLowerCase() || '';
                                    const orderedBy = order.orderedBy?.toLowerCase() || '';

                                    const labTestMatches = order.appointmentLabTests?.some((labTest: any) => {
                                        const testName = labTest.labTest?.name?.toLowerCase() || '';
                                        const testCode = labTest.labTest?.code?.toLowerCase() || '';
                                        const sampleType = labTest.labTest?.sampleType?.toLowerCase() || '';
                                        const status = labTest.status?.toLowerCase() || '';
                                        return testName.includes(searchQueryPatient.toLowerCase()) ||
                                            testCode.includes(searchQueryPatient.toLowerCase()) ||
                                            sampleType.includes(searchQueryPatient.toLowerCase()) ||
                                            status.includes(searchQueryPatient.toLowerCase());
                                    });

                                    return (
                                        patientName.includes(searchQueryPatient.toLowerCase()) ||
                                        patientId.includes(searchQueryPatient.toLowerCase()) ||
                                        phone.includes(searchQueryPatient) ||
                                        notes.includes(searchQueryPatient.toLowerCase()) ||
                                        orderedBy.includes(searchQueryPatient.toLowerCase()) ||
                                        labTestMatches
                                    );
                                })?.map((order: any) => (
                                    <TableRow key={order.id}>
                                        <TableCell>
                                            <div className="space-y-1">
                                                <div className="font-medium flex items-center gap-2">
                                                    {order.patient?.name}
                                                    <span className="px-2 py-1 rounded text-xs font-semibold bg-orange-100 text-orange-800">
                                                        {order.appointmentLabTests?.some((t: any) => t.processingType === "EXTERNAL") ? "External" : "Internal"}
                                                    </span>
                                                </div>
                                                <div className="text-sm text-artis-slate">
                                                    ID: {order.patient?.patientUniqueId}
                                                </div>
                                                <div className="text-sm text-artis-slate">
                                                    Phone: {order.patient?.phone}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="space-y-1 text-sm">
                                                <div>
                                                    <span className="text-gray-500">Created:</span>{" "}
                                                    {new Date(order.createdAt).toLocaleDateString()}
                                                </div>
                                                {order.urgentOrder && (
                                                    <div>
                                                        <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800">
                                                            URGENT
                                                        </span>
                                                    </div>
                                                )}
                                                {order.notes && (
                                                    <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                                        <strong>Notes:</strong> {order.notes}
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="space-y-1">
                                                {order.appointmentLabTests?.map((labTest: any) => (
                                                    <div key={labTest.id} className="flex items-center gap-2">
                                                        <span className="text-xs font-medium">{labTest.labTest?.name}:</span>
                                                        <span
                                                            className={`px-2 py-1 rounded text-xs font-semibold ${labTest.status === "PENDING"
                                                                ? "bg-yellow-100 text-yellow-800"
                                                                : labTest.status === "PROCESSING"
                                                                    ? "bg-blue-100 text-blue-800"
                                                                    : labTest.status === "COMPLETED"
                                                                        ? "bg-green-100 text-green-800"
                                                                        : labTest.status === "SENT_EXTERNAL"
                                                                            ? "bg-purple-100 text-purple-800"
                                                                            : "bg-gray-100 text-gray-800"
                                                                }`}
                                                        >
                                                            {labTest.status}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {order.status === LabOrderStatus.PENDING ? (
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleGenerateBill(order)}
                                                    className="bg-green-600 hover:bg-green-700"
                                                >
                                                    Generate Bill
                                                </Button>
                                            ) : (
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleViewBill(order)}
                                                    className="bg-blue-600 hover:bg-blue-700"
                                                >
                                                    View Bill
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {(!externalLabOrders || externalLabOrders.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                                            No lab orders found. Click "Create Lab Order" to add new orders.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    <CreateLabOrderDialog
                        open={createLabOrderDialogOpen}
                        onOpenChange={setCreateLabOrderDialogOpen}
                    />

                    <CreateLabOrderBill
                        labOrder={selectedLabOrder}
                        isOpen={billingDialogOpen}
                        onClose={() => setBillingDialogOpen(false)}
                        onSuccess={handleBillingSuccess}
                    />

                    <ViewBill
                        billId={selectedBillId}
                        isOpen={viewBillDialogOpen}
                        onClose={() => setViewBillDialogOpen(false)}
                        onSuccess={handleBillSuccess}
                    />
                </TabsContent>

                <TabsContent value="ipd">
                    <Card>
                        <CardHeader>
                            <CardTitle>Admitted IPD patients</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="max-w-lg">
                                <div className="text-sm font-medium mb-1">Search</div>
                                <Input
                                    placeholder="Search by name / UHID / phone / IPD ID / ward / bed"
                                    value={ipdPatientSearch}
                                    onChange={(e) => setIpdPatientSearch(e.target.value)}
                                />
                            </div>

                            {admissionsLoading ? (
                                <div className="text-sm text-muted-foreground">Loading…</div>
                            ) : filteredAdmissions.length === 0 ? (
                                <div className="text-sm text-muted-foreground">No admitted patients found.</div>
                            ) : (
                                <div className="rounded-lg border overflow-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Patient</TableHead>
                                                <TableHead>IPD</TableHead>
                                                <TableHead>Ward / Bed</TableHead>
                                                <TableHead className="text-right">Action</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {filteredAdmissions.map((a: any) => (
                                                <TableRow key={a.id}>
                                                    <TableCell>
                                                        <div className="font-medium">{a?.queue?.patient?.name ?? "—"}</div>
                                                        <div className="text-xs text-muted-foreground">
                                                            UHID: {a?.queue?.patient?.uhid ?? "—"} · {a?.queue?.patient?.phone ?? "—"}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>{a?.queue?.ipdNumber ?? a?.id}</TableCell>
                                                    <TableCell>
                                                        {(a?.wardName ?? a?.ward?.name ?? "—") + " / " + (a?.bedNumber ?? a?.bed?.bedNumber ?? "—")}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <Button onClick={() => openIpdOrderDialog(a)} variant="outline">
                                                            Order tests
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
                        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>
                                    Order lab tests
                                    {selectedAdmission && (
                                        <span className="block text-sm font-normal text-muted-foreground">
                                            {selectedAdmission?.queue?.patient?.name} ·{" "}
                                            {selectedAdmission?.queue?.patient?.uhid ?? "UHID —"} ·{" "}
                                            {selectedAdmission?.queue?.ipdNumber ?? selectedAdmission?.id}
                                        </span>
                                    )}
                                </DialogTitle>
                            </DialogHeader>

                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <div className="text-sm font-medium mb-1">Processing Type</div>
                                        <Select value={processingType} onValueChange={(v: any) => setProcessingType(v)}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="INTERNAL">Internal</SelectItem>
                                                <SelectItem value="EXTERNAL">External</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {processingType === "EXTERNAL" ? (
                                        <div>
                                            <div className="text-sm font-medium mb-1">External Vendor</div>
                                            <Select value={selectedExternalVendorId} onValueChange={setSelectedExternalVendorId}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select vendor" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {externalVendors.map((v: any) => (
                                                        <SelectItem key={v.id} value={v.id}>
                                                            {v.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    ) : (
                                        <div />
                                    )}
                                </div>

                                <div className="rounded-lg border overflow-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-[70px]">Add</TableHead>
                                                <TableHead>Test</TableHead>
                                                <TableHead className="text-right">Patient charge (₹)</TableHead>
                                                {processingType === "EXTERNAL" && (
                                                    <TableHead className="text-right">Hospital cost (₹)</TableHead>
                                                )}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {labTestsMaster.map((t: any) => {
                                                const checked = selectedLabTestIds.includes(t.id);
                                                return (
                                                    <TableRow key={t.id}>
                                                        <TableCell>
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={(e) => {
                                                                    const next = e.target.checked;
                                                                    setSelectedLabTestIds((prev) =>
                                                                        next ? Array.from(new Set([...prev, t.id])) : prev.filter((id) => id !== t.id)
                                                                    );
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="font-medium">{t.name}</div>
                                                            <div className="text-xs text-muted-foreground">
                                                                {t.code} · {t.sampleType}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-right">₹{Number(t.charge ?? 0).toFixed(2)}</TableCell>
                                                        {processingType === "EXTERNAL" && (
                                                            <TableCell className="text-right">
                                                                <Input
                                                                    inputMode="decimal"
                                                                    placeholder="0"
                                                                    value={externalCostByTestId[t.id] ?? ""}
                                                                    onChange={(e) => setExternalCostByTestId((p) => ({ ...p, [t.id]: e.target.value }))}
                                                                    disabled={!checked}
                                                                    className="text-right"
                                                                />
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>

                            <DialogFooter>
                                <Button variant="outline" onClick={() => setOrderDialogOpen(false)} disabled={createIpdTestsMutation.isPending}>
                                    Cancel
                                </Button>
                                <Button onClick={() => createIpdTestsMutation.mutate()} disabled={createIpdTestsMutation.isPending}>
                                    {createIpdTestsMutation.isPending ? "Ordering…" : "Order tests"}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </TabsContent>
            </Tabs>
        </div>
    );
}
